import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { createPost, updatePost, deletePost, revertPost } from './data';
import { useNotification } from '../../../../../shared/Notification/useNotification';
import { parseDate } from '../../../utils/helpers';
import { SCHEDULED } from '../../../utils/constants';

export const useCreatePostMutation = () => {
  const { t } = useTranslation('posts');
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();

  const { mutate: createPostMutation } = useMutation(createPost, {
    onSuccess: ({ data }) => {
      const { publishDate } = data.post;

      const content = publishDate
        ? t('createNotification.content', { date: parseDate(publishDate) })
        : t('createNotification.newPost');

      showSuccess({
        content,
        title: t('createNotification.title'),
      });

      queryClient.invalidateQueries(['campaignData']);
      queryClient.invalidateQueries(['shelfTiles']);
    },
  });

  return { createPostMutation };
};

export const useUpdatePostMutation = () => {
  const { t } = useTranslation('posts');
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const { mutate } = useMutation(updatePost, {
    onSuccess: () => {
      queryClient.invalidateQueries(['campaignData']);
      queryClient.invalidateQueries(['shelfTiles']);
    },
  });

  const updatePostMutation = values => {
    mutate(values, {
      onSuccess: ({ data }) => {
        const { publishDate, tiles } = data.post;
        const showReminderError = data.show_reminder_error;

        if (values.makeLive) {
          showSuccess({
            content: t('confirmationModal.launchNotification.content'),
            title: t('confirmationModal.launchNotification.title'),
          });
        } else if (data.post.status === SCHEDULED) {
          showSuccess({
            content: t('confirmationModal.successNotification.content'),
            title: t('confirmationModal.successNotification.title', {
              tileCount: tiles.length,
            }),
          });
        } else {
          showSuccess({
            content: t('updatedNotification.content', {
              date: parseDate(publishDate),
            }),
            title: t('updatedNotification.title'),
          });
        }

        if (showReminderError) {
          showError({
            title: t('reminderErrorNotification.title'),
            content: t('reminderErrorNotification.content'),
          });
        }
      },
    });
  };

  return { updatePostMutation, mutate };
};

export const useDeletePostMutation = () => {
  const queryClient = useQueryClient();

  const { mutate: deletePostMutation } = useMutation(deletePost, {
    onSuccess: () => {
      queryClient.invalidateQueries(['campaignData']);
      queryClient.invalidateQueries(['shelfTiles']);
    },
  });

  return { deletePostMutation };
};

export const useRevertPostMutation = () => {
  const { t } = useTranslation('posts');
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();

  const { mutate } = useMutation(revertPost, {
    onSuccess: () => {
      queryClient.invalidateQueries(['campaignData']);
      queryClient.invalidateQueries(['shelfTiles']);
    },
  });

  const revertPostMutation = values => {
    mutate(values, {
      onSuccess: ({ data }) => {
        const { publishDate } = data.post;

        showSuccess({
          content: t('updatedNotification.content', {
            date: parseDate(publishDate),
          }),
          title: t('updatedNotification.title'),
        });
      },
    });
  };

  return { revertPostMutation, mutate };
};
